import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartData, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { TranslateService } from '@ngx-translate/core';
import { CHART_AVERAGE_COLOR, CHART_COLORS } from '../chart-utils/chart-colors.const';
import { observeChartResize } from '../chart-utils/chart-resize.util';
import { trackByIndex, withAlphaColor } from '../chart-utils/chart-legend.util';

export interface ChartSeriesPoint {
  intervalStart: string;
  groupId: number;
  groupLabel: string;
  quantity: number;
}


@Component({
  selector: 'app-chart-line-bar',
  templateUrl: './chart-line-bar.component.html',
  styleUrls: ['./chart-line-bar.component.scss'],
changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChartLineBarComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() points: ChartSeriesPoint[] = [];
  @Input() granularity: 'hour' | 'day' = 'hour';
  @Input() aggregateTotal = false;
  @Input() color: 'blue' | 'green' = 'blue';
  @Input() chartType: 'line' | 'bar' = 'line';
  @Input() showDateInLabel = false;
  @Input() showDayBoundaryMarker = false;
  @Input() yMax?: number;
  @Input() rangeStart?: string;
  @Input() rangeEnd?: string;
  @Input() bucketDates?: string[];
  @Input() emphasizedGroupLabel?: string;
  @Input() averageLabel = 'Média';
  @Input() noDataLabel = 'Sem dados';

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;

  lineChartData: ChartData<'line' | 'bar'> = { labels: [], datasets: [] };
  lineChartOptions: ChartConfiguration<'line' | 'bar'>['options'];
  chartPlugins: Plugin<'line' | 'bar'>[] = [];
  legendItems: { text: string; color: string; dimmed: boolean }[] = [];
  selectedLegendIndices = new Set<number>();

  readonly trackByIndex = trackByIndex;

  private seriesColors: string[] = [];
  private emphasizedDatasetIndex: number | null = null;

  private stopResizeObserver?: () => void;

  constructor(private translateService: TranslateService, private elementRef: ElementRef<HTMLElement>) {
    this.lineChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        x: {
          grid: { color: '#F5F5F5' },
          ticks: { autoSkip: true, maxRotation: 0 }
        },
        y: {
          beginAtZero: true,
          grid: { color: '#F0F0F0' },
          ticks: { precision: 0 }
        }
      },
      elements: {
        point: { radius: 2, hoverRadius: 5 }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...this.tooltipInteractionOptions(),
          filter: (tooltipItem) => this.selectedLegendIndices.size === 0 || this.selectedLegendIndices.has(tooltipItem.datasetIndex)
        }
      },
      layout: {
        padding: { bottom: 4 }
      }
    };
  }

  ngAfterViewInit(): void {
    this.stopResizeObserver = observeChartResize(this.elementRef.nativeElement, () => this.chartDirective);
  }

  ngOnDestroy(): void {
    this.stopResizeObserver?.();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartType'] && this.lineChartOptions?.plugins?.tooltip) {
      Object.assign(this.lineChartOptions.plugins.tooltip, this.tooltipInteractionOptions());
    }
    if (changes['points'] || changes['aggregateTotal'] || changes['showDateInLabel'] || changes['showDayBoundaryMarker']
      || changes['chartType'] || changes['rangeStart'] || changes['rangeEnd'] || changes['bucketDates'] || changes['emphasizedGroupLabel']
      || changes['averageLabel']) {
      this.buildChartData();
    }
    if (changes['yMax'] && this.lineChartOptions?.scales?.['y']) {
      this.lineChartOptions.scales['y'].max = this.yMax !== undefined ? Math.ceil(this.yMax * 1.2) : undefined;
      this.chartDirective?.update();
    }
  }

  get hasData(): boolean {
    return this.points.length > 0;
  }

  get legendFocusEnabled(): boolean {
    return this.legendItems.length > 1;
  }

  onLegendItemClick(index: number): void {
    if (!this.legendFocusEnabled) return;
    const next = new Set(this.selectedLegendIndices);
    if (next.has(index)) next.delete(index); else next.add(index);
    this.selectedLegendIndices = next.size === this.legendItems.length ? new Set() : next;
    this.applyLegendHighlight();
  }

  private round2(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private tooltipInteractionOptions(): { mode: 'point' | 'index'; intersect: boolean } {
    return this.chartType === 'bar' ? { mode: 'index', intersect: false } : { mode: 'point', intersect: true };
  }

  private buildChartData(): void {
    this.selectedLegendIndices = new Set();
    this.seriesColors = [];

    const buckets = this.computeBuckets();
    const color = CHART_COLORS[this.color];
    const isBar = this.chartType === 'bar';

    if (this.aggregateTotal) {
      const totals = new Map<string, number>();
      for (const point of this.points) {
        totals.set(point.intervalStart, (totals.get(point.intervalStart) ?? 0) + point.quantity);
      }
      const data = buckets.map(b => totals.get(b) ?? 0);
      const totalLabel = this.translateService.instant('total');

      const datasets: ChartData<'line' | 'bar'>['datasets'] = [
        isBar
          ? { label: totalLabel, data, backgroundColor: color, borderRadius: 4, maxBarThickness: 28, barPercentage: 0.7, categoryPercentage: 0.8, order: 2 }
          : { label: totalLabel, data, borderColor: color, backgroundColor: color + '26', fill: 'origin', tension: 0.3 }
      ];

      if (isBar && data.some(value => value > 0)) {
        const average = this.round2(data.reduce((sum, value) => sum + value, 0) / data.length);
        datasets.push({
          type: 'line',
          label: this.averageLabel,
          data: buckets.map(() => average),
          borderColor: CHART_AVERAGE_COLOR,
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          order: 1
        });
      }

      this.lineChartData.labels = buckets.map(b => this.formatBucket(b));
      this.lineChartData.datasets = datasets;
      this.legendItems = [];
      this.chartPlugins = this.buildPlugins(buckets);
      this.chartDirective?.chart?.update();
      return;
    }

    const groups = new Map<number, { id: number; label: string; values: Map<string, number> }>();

    for (const point of this.points) {
      if (!groups.has(point.groupId)) {
        groups.set(point.groupId, { id: point.groupId, label: point.groupLabel, values: new Map() });
      }
      groups.get(point.groupId)!.values.set(point.intervalStart, point.quantity);
    }

    const entries = Array.from(groups.values()).map(group => {
      const data = buckets.map(b => group.values.get(b) ?? 0);
      const total = this.round2(data.reduce((sum, value) => sum + value, 0));
      return { ...group, data, total };
    });

    entries.sort((a, b) => this.legendRank(a.label) - this.legendRank(b.label) || b.total - a.total);

    this.emphasizedDatasetIndex = null;
    const colors = entries.map((entry, index) => {
      const isEmphasized = !!this.emphasizedGroupLabel && entry.label === this.emphasizedGroupLabel;
      return isEmphasized ? CHART_AVERAGE_COLOR : this.seriesColor(index);
    });
    this.seriesColors = colors;

    // lineChartData is mutated in place: reassigning it would trigger a second animated ng2-charts update
    this.lineChartData.labels = buckets.map(b => this.formatBucket(b));
    this.lineChartData.datasets = entries.map((entry, index) => {
      const isEmphasized = !!this.emphasizedGroupLabel && entry.label === this.emphasizedGroupLabel;
      if (isEmphasized) this.emphasizedDatasetIndex = index;
      const label = `${entry.label} (${entry.total})`;
      const seriesColor = colors[index];

      if (isBar) {
        return { label, data: entry.data, backgroundColor: seriesColor, borderRadius: 4, maxBarThickness: 28, barPercentage: 0.7, categoryPercentage: 0.8 };
      }

      return {
        label,
        data: entry.data,
        borderColor: seriesColor,
        backgroundColor: seriesColor,
        pointBackgroundColor: seriesColor,
        pointBorderColor: seriesColor,
        fill: false,
        tension: 0.2,
        borderDash: isEmphasized ? [6, 4] : undefined,
        borderWidth: isEmphasized ? 3 : undefined
      };
    });
    this.legendItems = entries.map((entry, index) => ({
      text: `${entry.label} (${entry.total})`,
      color: colors[index],
      dimmed: false
    }));
    this.chartPlugins = this.buildPlugins(buckets);
    this.chartDirective?.chart?.update();
  }

  private seriesColor(index: number): string {
    const hue = (index * 137.508) % 360;
    const [r, g, b] = this.hslToRgb(hue, 65, 50);
    return `rgb(${r}, ${g}, ${b})`;
  }

  private hslToRgb(h: number, s: number, l: number): [number, number, number] {
    const sFrac = s / 100;
    const lFrac = l / 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = sFrac * Math.min(lFrac, 1 - lFrac);
    const f = (n: number) => lFrac - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
  }

  private applyLegendHighlight(): void {
    const datasets = this.lineChartData.datasets;
    if (!datasets.length || datasets.length !== this.seriesColors.length) return;

    const hasSelection = this.selectedLegendIndices.size > 0;

    this.lineChartData.datasets = datasets.map((dataset, i) => {
      const isFocused = this.selectedLegendIndices.has(i);
      const isVisible = !hasSelection || isFocused;
      const alpha = isVisible ? 1 : 0.15;
      const isAverage = i === this.emphasizedDatasetIndex;

      const fadedColor = withAlphaColor(this.seriesColors[i], alpha);

      return {
        ...dataset,
        borderColor: fadedColor,
        backgroundColor: fadedColor,
        pointBackgroundColor: fadedColor,
        pointBorderColor: fadedColor,
        borderWidth: isFocused ? 4 : (isAverage ? 3 : undefined),
        pointRadius: isFocused ? 4 : undefined,
        pointBorderWidth: isFocused ? 0 : undefined,
        borderDash: (isAverage && !isFocused) ? [6, 4] : undefined
      };
    });

    this.legendItems = this.legendItems.map((item, i) => ({
      ...item,
      dimmed: hasSelection && !this.selectedLegendIndices.has(i)
    }));
    this.chartDirective?.chart?.update('none');
  }

  private legendRank(label: string): number {
    return !!this.emphasizedGroupLabel && label === this.emphasizedGroupLabel ? 1 : 0;
  }

  private computeDayBoundaryIndex(buckets: string[]): number | null {
    for (let i = 1; i < buckets.length; i++) {
      if (buckets[i].substring(0, 10) !== buckets[i - 1].substring(0, 10)) return i;
    }
    return null;
  }

  private formatDateLabel(isoDate: string): string {
    return `${isoDate.substring(8, 10)}/${isoDate.substring(5, 7)}`;
  }

  private buildPlugins(buckets: string[]): Plugin<'line' | 'bar'>[] {
    const plugins: Plugin<'line' | 'bar'>[] = [];

    if (this.showDayBoundaryMarker && this.granularity !== 'day') {
      const boundaryIndex = this.computeDayBoundaryIndex(buckets);
      if (boundaryIndex !== null) {
        plugins.push(this.dayBoundaryMarkerPlugin(buckets, boundaryIndex));
      }
    }

    return plugins;
  }

  private dayBoundaryMarkerPlugin(buckets: string[], boundaryIndex: number): Plugin<'line' | 'bar'> {
    const beforeLabel = this.formatDateLabel(buckets[boundaryIndex - 1]);
    const afterLabel = this.formatDateLabel(buckets[boundaryIndex]);

    return {
      id: 'dayBoundaryMarker',
      afterDraw: (chart) => {
        const xScale = chart.scales['x'];
        if (!xScale) return;
        const { top, bottom } = chart.chartArea;
        const x = (xScale.getPixelForValue(boundaryIndex - 1) + xScale.getPixelForValue(boundaryIndex)) / 2;
        const ctx = chart.ctx;

        ctx.save();
        ctx.strokeStyle = '#98A2B3';
        ctx.setLineDash([4, 4]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.font = '11px sans-serif';
        ctx.textBaseline = 'middle';
        const labelY = top + 12;
        this.drawLabelChip(ctx, beforeLabel, x - 8, labelY, 'right');
        this.drawLabelChip(ctx, afterLabel, x + 8, labelY, 'left');
        ctx.restore();
      }
    };
  }

  private drawLabelChip(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, align: 'left' | 'right'): void {
    const paddingX = 4;
    const textWidth = ctx.measureText(text).width;
    const boxX = align === 'right' ? x - textWidth - paddingX : x - paddingX;
    const boxWidth = textWidth + paddingX * 2;
    const boxHeight = 14;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.fillRect(boxX, y - boxHeight / 2, boxWidth, boxHeight);

    ctx.fillStyle = '#667085';
    ctx.textAlign = align;
    ctx.fillText(text, x, y);
  }

  private computeBuckets(): string[] {
    const distinct = Array.from(new Set(this.points.map(p => p.intervalStart))).sort();
    if (this.granularity === 'day') {
      if (this.bucketDates?.length) return this.bucketDates;
      if (!this.rangeStart || !this.rangeEnd) return distinct;
      const buckets: string[] = [];
      let cursor = this.rangeStart;
      while (cursor <= this.rangeEnd) {
        buckets.push(cursor);
        cursor = this.shiftBucket(cursor, 1440);
      }
      return buckets;
    }

    let first: string;
    let last: string;

    if (this.rangeStart && this.rangeEnd) {
      first = this.floorToHalfHour(this.rangeStart);
      last = this.floorToHalfHour(this.rangeEnd);
    } else {
      if (!distinct.length) return distinct;
      first = this.shiftBucket(distinct[0], -30);
      last = this.shiftBucket(distinct[distinct.length - 1], 30);
    }

    const buckets: string[] = [];
    let cursor = first;
    while (cursor <= last) {
      buckets.push(cursor);
      cursor = this.shiftBucket(cursor, 30);
    }
    return buckets;
  }

  private floorToHalfHour(isoDateTime: string): string {
    const [datePart, timePart] = isoDateTime.split('T');
    const [hour, minute] = timePart.split(':').map(Number);
    const flooredMinute = minute < 30 ? '00' : '30';
    return `${datePart}T${String(hour).padStart(2, '0')}:${flooredMinute}:00`;
  }

  private shiftBucket(isoDateTime: string, minutesDelta: number): string {
    const [datePart, timePart] = isoDateTime.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    const totalMinutes = hour * 60 + minute + minutesDelta;
    const dayDelta = Math.floor(totalMinutes / 1440);
    const minutesOfDay = ((totalMinutes % 1440) + 1440) % 1440;

    const anchor = new Date(Date.UTC(year, month - 1, day));
    anchor.setUTCDate(anchor.getUTCDate() + dayDelta);

    const newYear = anchor.getUTCFullYear();
    const newMonth = String(anchor.getUTCMonth() + 1).padStart(2, '0');
    const newDay = String(anchor.getUTCDate()).padStart(2, '0');
    const newHour = String(Math.floor(minutesOfDay / 60)).padStart(2, '0');
    const newMinute = String(minutesOfDay % 60).padStart(2, '0');

    return `${newYear}-${newMonth}-${newDay}T${newHour}:${newMinute}:00`;
  }

  private formatBucket(isoDate: string): string {
    if (this.granularity === 'day') {
      return isoDate.substring(8, 10) + '/' + isoDate.substring(5, 7);
    }
    const time = isoDate.substring(11, 16);
    return this.showDateInLabel ? `${isoDate.substring(8, 10)}/${isoDate.substring(5, 7)} ${time}` : time;
  }
}
